import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import {
  ConfirmSmsVerificationRequest,
  GrantSmsConsentRequest,
  NotificationSettings,
  NotificationRecipient,
  NotificationRecipientCreateRequest,
  NotificationRecipientUpdateRequest,
  OwnerProfileSettings,
  PasswordChangeRequest,
  SettingsMutationResponse,
  StartSmsVerificationRequest,
  StripePaymentSetupIntent,
  StripePaymentSetupResult,
  SubscriptionSettings,
  SmsSettingsDescriptor,
  UpdateSmsSettingsRequest
} from '@app/core/models';
import { environment } from '@env/environment';

@Injectable({ providedIn: 'root' })
export class SettingsApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiBaseUrl}/settings`;

  getProfile() {
    return this.http.get<OwnerProfileSettings>(`${this.baseUrl}/profile`);
  }

  updateProfile(payload: OwnerProfileSettings) {
    return this.http.put<OwnerProfileSettings>(`${this.baseUrl}/profile`, payload);
  }

  updatePassword(payload: PasswordChangeRequest) {
    return this.http.post<SettingsMutationResponse>(`${this.baseUrl}/password`, payload);
  }

  getNotifications() {
    return this.http.get<NotificationSettings>(`${this.baseUrl}/notifications`);
  }

  updateNotifications(payload: NotificationSettings) {
    return this.http.put<NotificationSettings>(`${this.baseUrl}/notifications`, payload);
  }

  getNotificationRecipients() {
    return this.http.get<NotificationRecipient[]>(`${this.baseUrl}/notification-recipients`);
  }

  createNotificationRecipient(payload: NotificationRecipientCreateRequest) {
    return this.http.post<NotificationRecipient>(`${this.baseUrl}/notification-recipients`, payload);
  }

  updateNotificationRecipient(recipientId: string, payload: NotificationRecipientUpdateRequest) {
    return this.http.put<NotificationRecipient>(
      `${this.baseUrl}/notification-recipients/${recipientId}`,
      payload
    );
  }

  deleteNotificationRecipient(recipientId: string) {
    return this.http.delete<void>(`${this.baseUrl}/notification-recipients/${recipientId}`);
  }

  getSmsSettingsDescriptor() {
    return this.http.get<SmsSettingsDescriptor>(`${this.baseUrl}/notification-recipients/sms/descriptor`);
  }

  grantSmsConsent(recipientId: string, payload: GrantSmsConsentRequest) {
    return this.http.post<NotificationRecipient>(
      `${this.baseUrl}/notification-recipients/${recipientId}/sms/consent`,
      payload
    );
  }

  revokeSmsConsent(recipientId: string, rowVersion: string) {
    return this.http.delete<NotificationRecipient>(
      `${this.baseUrl}/notification-recipients/${recipientId}/sms/consent`,
      { params: { rowVersion } }
    );
  }

  startSmsVerification(recipientId: string, payload: StartSmsVerificationRequest) {
    return this.http.post<NotificationRecipient>(
      `${this.baseUrl}/notification-recipients/${recipientId}/sms/verification/start`,
      payload
    );
  }

  confirmSmsVerification(recipientId: string, payload: ConfirmSmsVerificationRequest) {
    return this.http.post<NotificationRecipient>(
      `${this.baseUrl}/notification-recipients/${recipientId}/sms/verification/confirm`,
      payload
    );
  }

  updateSmsSettings(recipientId: string, payload: UpdateSmsSettingsRequest) {
    return this.http.put<NotificationRecipient>(
      `${this.baseUrl}/notification-recipients/${recipientId}/sms/settings`,
      payload
    );
  }


  getSubscription() {
    return this.http.get<SubscriptionSettings>(`${this.baseUrl}/subscription`);
  }

  getSubscriptionPreview() {
    return this.http.get<SubscriptionSettings>(`${this.baseUrl}/subscription/preview`);
  }

  createPaymentMethodSetupIntent() {
    return this.http.post<StripePaymentSetupIntent>(`${this.baseUrl}/subscription/setup-intent`, {});
  }

  attachPaymentMethod(payload: StripePaymentSetupResult) {
    return this.http.post<SubscriptionSettings>(`${this.baseUrl}/subscription/payment-methods`, payload);
  }

  removePaymentMethod(paymentMethodId: string) {
    return this.http.delete<SubscriptionSettings>(
      `${this.baseUrl}/subscription/payment-methods/${paymentMethodId}`
    );
  }

  cancelSubscription() {
    return this.http.post<SubscriptionSettings>(`${this.baseUrl}/subscription/cancel`, {});
  }
}
